/**
 * Auth context.
 *
 * Wraps the shared fluidvip Supabase session in a small React context so the
 * shell can gate on it and API calls can read the access token. The Supabase SDK
 * owns persistence and refresh; we mirror its session into state via
 * onAuthStateChange and otherwise stay out of the way.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabase } from './supabase';
import { AUTH_CONFIGURED } from './env';

export interface AuthState {
  /** Initial session resolution is complete — prevents a login flash on reload. */
  ready: boolean;
  /** False when Supabase env vars are absent (local dev without auth). */
  configured: boolean;
  session: Session | null;
  user: User | null;
  signInWithPassword: (email: string, password: string, captchaToken?: string) => Promise<{ error?: string }>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** Google OAuth returns here; the SPA exchanges the code and tidies the URL. */
export const AUTH_CALLBACK_PATH = '/auth/callback';

export async function signInWithPasswordAction(
  email: string,
  password: string,
  captchaToken?: string,
): Promise<{ error?: string }> {
  const supabase = getSupabase();
  if (!supabase) return { error: 'Authentication is not configured.' };
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
    ...(captchaToken ? { options: { captchaToken } } : {}),
  });
  if (error) return { error: error.message };
  // Defence in depth — GoTrue blocks this already.
  if (data.user && !data.user.email_confirmed_at) {
    await supabase.auth.signOut();
    return { error: 'Please confirm your email before signing in.' };
  }
  return {};
}

export async function signInWithGoogleAction(): Promise<{ error?: string }> {
  const supabase = getSupabase();
  if (!supabase) return { error: 'Authentication is not configured.' };
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}${AUTH_CALLBACK_PATH}` },
  });
  return error ? { error: error.message } : {};
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(!AUTH_CONFIGURED);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) {
      setReady(true);
      return;
    }
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      configured: AUTH_CONFIGURED,
      session,
      user: session?.user ?? null,
      signInWithPassword: signInWithPasswordAction,
      signInWithGoogle: signInWithGoogleAction,
      async signOut() {
        const supabase = getSupabase();
        await supabase?.auth.signOut();
        setSession(null);
      },
    }),
    [ready, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
